import * as yup from 'yup';


interface ValidateOptions {
  index: number,
  from: Array<{
    value: {
      items: Array<{
        selectedItemGroup: string,
        fixedPrice: number,
        sku: string,
        qty: number,
        additional_subscription_price: number,
        savings: number,
        discount: string
        discountType: string,
        subscriptionFrequency: string,
        subscription_id: number
      }>
    }
  }>
}

export const createValidationSchema = (
  quoteProductsByGroup,
  customerPushCart,
  subscriptionOptionsBySku,
  handleSelectDataSource,
  isPatientGroupActive,
  currentMarketingSegment,
  calculatedTotals) => {
  return yup.object().shape({
    selectedGroup: yup
      .string()
      .test('selectedGroup', 'Group is required', (val, options) => {
        const isNoSelectItem = options.parent.items.find((i) => !i.sku);

        if (isNoSelectItem) {
          return !!val;
        }

        return true;
      }),
    items: yup.array().of(
      yup.object().shape({
        sku: yup
          .string()
          .when(
            'selectedItemGroup',
            {
              is: (val) => !!val,
              then: yup
                .string()
                .required('Field is required')
                .test('sku', 'Subscription configs are wrong', function (value) {
                  const { selectedItemGroup } = this.parent;
                  const product = quoteProductsByGroup?.[selectedItemGroup]?.find((el) => el.sku == value);
                  const isSubscriptionActive =
                    product?.custom_attributes.find((attr) => attr.attribute_code === 'subscription_active')
                      ?.value == 1;

                  if (!product) {
                    return false;
                  }

                  const {
                    subscriptionOptions
                  } = handleSelectDataSource(
                    product?.sku,
                    false,
                    customerPushCart,
                    subscriptionOptionsBySku,
                    product.custom_attributes,
                    []
                  );

                  const isWrongSubscriptionConfigs =
                    isSubscriptionActive && !subscriptionOptions?.length;

                  return !isWrongSubscriptionConfigs;
                })
            }
          ),
        qty: yup
          .number()
          .typeError('Field is required')
          .integer('Only integer numbers allowed')
          .min(1, 'Only positive values allowed')
          .test('max', 'There is not enough items in stock', (value, context) => {
            const itemIndex = (context.options as ValidateOptions)?.index;
            const allItems = (context.options as ValidateOptions).from[1].value.items;

            const {
              selectedItemGroup,
              sku,
            } = allItems[itemIndex];

            const totalQty = allItems.reduce((sum, item) => {
              if (sku === item.sku) {
                return sum + item.qty;
              }

              return sum;
            }, 0);

            const product = quoteProductsByGroup?.[selectedItemGroup]?.find((el) => el.sku == sku);
            const customersTotalCount = isPatientGroupActive ? currentMarketingSegment?.total_count : 1;

            if ((product?.type_id === 'simple') && ((value * customersTotalCount) > (product?.extension_attributes.stock_item.qty - totalQty + value))) {
              return false;
            }

            return true;
          })
          .required('Field is required'),
        fixedPrice: yup
          .number()
          .typeError('Allowed only numerical symbols')
          .positive('Value must be positive')
          .min(0, 'Price should be positive number')
          .test(
            'price',
            'The unit price can\'t be lower discount', function (val) {
              const { discountType, discount, sku, savings, qty } = this.parent;

              if (sku?.includes('bdsku')) {
                return true;
              }

              if (discountType === 'fixed' && discount > val) {
                return false;
              }

              if (!discount && Math.abs(savings / qty) > val) {
                return false;
              }

              return true;
            }
          )
          .required('Field is required'),
        discountType: yup.string(),
        discount: yup.mixed().when(['discountType', 'fixedPrice'], {
          is: (discountType, fixedPrice) => !!discountType && fixedPrice !== undefined,
          then: yup.mixed().required('Field is required').test('discountValidation', 'Invalid Discount', function (value) {
            const { discountType, fixedPrice, sku } = this.parent;

            if (!value || sku?.includes('bdsku')) {
              if (calculatedTotals.total < 0) {
                return this.createError({ message: 'Discount can\'t be more than total'});
              }

              return true;
            }

            const formattedFixedPrice = fixedPrice?.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});

            if (discountType === 'percent') {
              if (isNaN(value) || value < 0 || value > 100) {
                return this.createError({ message: 'Discount must be between 0 and 100' });
              }
            } else if (isNaN(value) || value < 0 || value > fixedPrice) {
              return this.createError({ message: `Discount must be between 0 and $${formattedFixedPrice}` });
            }

            return true;
          }),
          otherwise: yup.mixed(),
        }),
        subscription_id: yup
          .string()
          .when(
            'sku',
            {
              is: (val) => !!val,
              then: yup
                .string()
                .test(
                  'subscription_id',
                  'Field is required', function (val) {
                    const { selectedItemGroup, sku } = this.parent;
                    const product = quoteProductsByGroup?.[selectedItemGroup]?.find((el) => el.sku == sku);

                    if (!product) {
                      return false;
                    }

                    const {
                      subscriptionOptions
                    } = handleSelectDataSource(
                      product?.sku,
                      false,
                      customerPushCart,
                      subscriptionOptionsBySku,
                      product.custom_attributes,
                      []
                    );

                    const isSubscriptionActive = product?.custom_attributes?.find(
                      (attr) => attr.attribute_code === 'subscription_active'
                    )?.value == 1;

                    if (subscriptionOptions?.length === 0) {
                      return true;
                    }

                    if (subscriptionOptions?.length > 0 && isSubscriptionActive) {
                      return !!val?.length;
                    }

                    return true;
                  }
                )
            }
          )
      })
    ),
  });
};
